import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg } from "@/lib/api";
import { eur, itDate } from "@/lib/pricing";

const KINDS = [["cliente", "Fatturato per cliente"], ["categoria", "Fatturato per categoria"], ["mese", "Fatturato per mese"],
  ["argomento", "Fatturato per argomento (#parole nelle note)"], ["margine", "Margine per preventivo"], ["persi", "Preventivi persi / rifiutati"]];

const fmt = (v, type) => {
  if (type === "money") return eur(v);
  if (type === "pct") return `${(+v || 0).toFixed(2).replace(".", ",")}%`;
  if (type === "date") return itDate(v) || "\u2014";
  return v ?? "\u2014";
};
const right = (t) => (t === "money" || t === "pct" || t === "int" ? "text-right font-mono whitespace-nowrap" : "");

const Bar = ({ value, max }) => (
  <div className="h-1.5 mt-1 rounded bg-slate-800 overflow-hidden"><div className="h-full bg-amber-400/80" style={{ width: `${max ? Math.max((value / max) * 100, 2) : 0}%` }} /></div>
);

const ResultTable = ({ res }) => {
  const money = res.columns.find((c) => c.type === "money");
  const max = Math.max(0, ...res.rows.map((r) => +r[money?.key] || 0));
  return (
    <table className="w-full text-left border-collapse" data-testid="analytics-table">
      <thead><tr className="border-b border-slate-700 bg-slate-950/60">
        {res.columns.map((c) => <th key={c.key} className={`py-3 px-3 micro-label whitespace-nowrap ${right(c.type)}`}>{c.label}</th>)}
      </tr></thead>
      <tbody>{res.rows.map((r, i) => (
        <tr key={i} className="border-b border-slate-800/70 hover:bg-slate-800/30 transition-colors" data-testid={`analytics-row-${i}`}>
          {res.columns.map((c, j) => (
            <td key={c.key} className={`py-2.5 px-3 text-sm ${right(c.type)} ${c.key === "crediti_iva" ? "text-red-300" : ""}`}>
              {fmt(r[c.key], c.type)}{j === 0 && money && <Bar value={+r[money.key] || 0} max={max} />}
            </td>
          ))}
        </tr>
      ))}</tbody>
      <tfoot><tr className="bg-amber-400 text-slate-950 border-y-2 border-amber-300" data-testid="analytics-total">
        {res.columns.map((c, j) => (
          <td key={c.key} className={`py-4 px-3 text-lg font-extrabold ${right(c.type)}`} data-testid={`analytics-total-${c.key}`}>
            {j === 0 ? "TOTALE" : res.totals[c.key] !== undefined ? fmt(res.totals[c.key], c.type) : ""}
          </td>
        ))}
      </tr></tfoot>
    </table>
  );
};

export const AnalyticsPanel = () => {
  const api = useApi();
  const [kind, setKind] = useState("cliente");
  const [dfrom, setFrom] = useState("");
  const [dto, setTo] = useState("");
  const [res, setRes] = useState(null);
  const [dl, setDl] = useState(false);
  const params = { kind, ...(dfrom && { dfrom }), ...(dto && { dto }) };
  useEffect(() => {
    api.get("/analytics", { params }).then((r) => setRes(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [kind, dfrom, dto]); // eslint-disable-line react-hooks/exhaustive-deps
  const download = async () => {
    setDl(true);
    try {
      const r = await api.get("/analytics-excel", { params, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `Report_${kind}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch (e) { toast.error(errMsg(e, "Download non riuscito")); } finally { setDl(false); }
  };
  return (
    <div className="space-y-4" data-testid="analytics-panel">
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1.5 min-w-[240px] flex-1 sm:flex-none"><Label className="micro-label">Report</Label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="sm:w-80" data-testid="analytics-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>{KINDS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`analytics-kind-${k}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label className="micro-label">Evento dal</Label><Input type="date" lang="it-IT" value={dfrom} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="analytics-from" /></div>
        <div className="space-y-1.5"><Label className="micro-label">al</Label><Input type="date" lang="it-IT" value={dto} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="analytics-to" /></div>
        <Button variant="outline" onClick={download} disabled={dl} className="sm:ml-auto" data-testid="analytics-excel-btn">
          {dl ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}Scarica Excel
        </Button>
      </div>
      <p className="text-xs text-slate-500">
        {kind === "persi" ? "Preventivi segnati come Rifiutato." : "Solo preventivi confermati, importi IVA esclusa."}
        {kind === "argomento" && " Scrivi nelle note del Report parole con # (es. #matrimonio #congresso): un preventivo con più argomenti conta in ognuno."}
      </p>
      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-x-auto">
        {!res ? <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-500" /></div>
          : res.rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400" data-testid="analytics-empty"><BarChart3 className="h-8 w-8 mx-auto mb-2 text-slate-600" />Nessun dato per questo report nel periodo scelto.</div>
          ) : <ResultTable res={res} />}
      </div>
    </div>
  );
};
